import { PathFinderService } from 'src/app/services/pathfinder/path-finder.service';
import { LOCATIONS, getLocation } from '../constants/locations.constant';
import { getPriceTypeByName, PRICE_TYPES, SALES_TAXES, SMUGGLER_FEE } from '../constants/market.constant';
import { Item, UpgradeResource } from './items.model';
import { Location } from './location.model';
import { PriceType } from './market.model';
import { Quality } from './quality.model';

//MARK: FlipperResponse
export class FlipperResponse {
  flips: MarketFlip[] = [];
  missedFlipsCount: number = 0;
  missedFlipsProfit: number = 0;
}

//MARK: MarketFlip
export class MarketFlip {
  smugglersBuyLocation?: Location;
  smugglersDistance: number = 0;
  smugglerFee: number = 0;
  id!: string;
  itemTypeId!: string;
  item: Item | undefined;
  buyOrder!: MarketOrder;
  sellOrder!: MarketOrder;
  createdAt!: Date;
  consumedAt?: Date;
  upgradeResources!: UpgradeResource[];
  server!: string | undefined;
  isUpgrade!: boolean;
  upgradeFrom!: number;
  upgradeTo!: number;
  isUnique: boolean = false;
  getAgeInMinutes(currentDate: Date): number {
    return (currentDate.getTime() - this.createdAt.getTime()) / 60000;
  }

  setSmugglersBuyLocation(buyLocation: Location | null, pathfinderService: PathFinderService) {
    const startTime = performance.now();

    if (!buyLocation) {
      this.smugglersBuyLocation = undefined;
      this.smugglersDistance = 0;
      this.smugglerFee = 0;
      return;
    }

    this.smugglersBuyLocation = buyLocation;
    this.smugglersDistance =
      pathfinderService.findShortestStepsByNeighbours(
        this.sellOrder.location.index.toString().padStart(4, '0'),
        buyLocation.index.toString().padStart(4, '0')
      ) ?? 0;
    this.smugglerFee = Math.ceil(this.smugglersDistance * SMUGGLER_FEE * this.sellOrder.unitPriceSilver);

    const duration = performance.now() - startTime;
  }

  get upgradeCost(): number {
    return this.upgradeResources.reduce((acc, resource) => acc + resource.price * resource.count, 0);
  }
  get profitPremium(): number {
    return this.buyOrder.unitPriceSilver * (1 - SALES_TAXES.premium.value) - this.sellOrder.unitPriceSilver - this.upgradeCost - this.smugglerFee;
  }
  get hasMissingResourcePrice(): boolean {
    return this.upgradeResources.some((resource) => !resource.price || resource.price === 0);
  }
  get profitNonPremium(): number {
    return this.buyOrder.unitPriceSilver * (1 - SALES_TAXES.normal.value) - this.sellOrder.unitPriceSilver - this.upgradeCost - this.smugglerFee;
  }
  get isConsumed(): boolean {
    return this.buyOrder.isConsumed || this.sellOrder.isConsumed;
  }

  get profitPercentagePremium(): number {
    return this.profitPremium / (this.sellOrder.unitPriceSilver + this.upgradeCost);
  }

  get profitPercentageNonPremium(): number {
    return this.profitNonPremium / (this.sellOrder.unitPriceSilver + this.upgradeCost);
  }
}

//MARK: MarketOrder
export class MarketOrder {
  id!: number;
  item!: Item | undefined;
  itemTypeId!: string;
  location!: Location;
  qualityLevel!: Quality;
  enchantmentLevel!: number;
  unitPriceSilver!: number;
  amount!: number;
  createdAt!: Date;
  expires!: Date;
  isConsumed!: boolean;
  server!: string;
  getAgeInMinutes(currentDate: Date): number {
    return (currentDate.getTime() - this.createdAt.getTime()) / 60000;
  }
}

//MARK: FlipsStatus
export interface FlipsStatus {
  server: string;
  totalFlips: number;
  totalFlipsConsumed: number;
  totalBuyOrders: number;
  totalBuyOrdersConsumed: number;
  totalSellOrders: number;
  totalSellOrdersConsumed: number;
  totalPremiumProfit: number;
  totalPremiumProfitConsumed: number;
}

//MARK: FlipperStats
export interface FlipperStats {
  flipsStatus: FlipsStatus[];
  flipLocationsStatus: FlipLocationStatus[];
}

//MARK: FlipsCounter
export interface FlipsCounter {
  id: string;
  dateTime: Date;
  server: string;
  foundNormalFlipsCount: number;
  foundUpgradeFlipsCount: number;
  foundNormalFlipsProfit: number;
  foundUpgradeFlipsProfit: number;
  consumedNormalFlipsCount: number;
  consumedUpgradeFlipsCount: number;
  consumedNormalFlipsProfit: number;
  consumedUpgradeFlipsProfit: number;
}

//MARK: MarketFlipRecord
export class MarketFlipRecord {
  id!: string;
  soldItem?: Item;
  boughtItem?: Item;
  flipTime!: Date;
  buyLocation!: Location;
  buyQuality!: Quality;
  buyPrice!: number;
  sellLocation!: Location;
  sellQuality!: Quality;
  sellPrice!: number;
  isUpgrade!: boolean;
  upgradeCost!: number;
  smugglersFee!: number;
  profitPremium!: number;
  profitNonPremium!: number;
  server!: string;
  failedFlip!: boolean;

  get profitPercentagePremium(): number {
    return this.profitPremium / (this.buyPrice + this.upgradeCost + this.smugglersFee);
  }

  get profitPercentageNonPremium(): number {
    return this.profitNonPremium / (this.buyPrice + this.upgradeCost + this.smugglersFee);
  }
}

//MARK: MarketFlipHistoryStats
export class MarketFlipHistoryStats {
  server!: string;
  topFlips!: MarketFlipRecord[];
  flipCount!: number;
  totalProfitPremium!: number;
  totalProfitNonPremium!: number;
}

//MARK: FlipLocationStatus
export class FlipLocationStatus {
  server!: string;
  buyLocation!: Location;
  count: number = 0;
  profitPremium: number = 0;
}

//MARK: UserFlipsResponse
export class UserFlipsResponse {
  flips: MarketFlipRecord[] = [];
  flipsCount: number = 0;
  normalFlipsCount: number = 0;
  upgradeFlipsCount: number = 0;
  totalProfitPremium: number = 0;
  totalProfitNormalPremium: number = 0;
  totalProfitUpgradePremium: number = 0;
  get averageProfitPremium(): number {
    return this.flipsCount > 0 ? this.totalProfitPremium / this.flipsCount : 0;
  }
  get averageProfitNormalPremium(): number {
    return this.normalFlipsCount > 0 ? this.totalProfitNormalPremium / this.normalFlipsCount : 0;
  }
  get averageProfitUpgradePremium(): number {
    return this.upgradeFlipsCount > 0 ? this.totalProfitUpgradePremium / this.upgradeFlipsCount : 0;
  }
}

//MARK: FlipperConfiguration
export class FlipperConfiguration {
  minProfit: number = 5000;
  minProfitPercentage: number = 0;
  maxBuyOrderAge: number | null = 30;
  maxSellOrderAge: number | null = 60;
  userFlips: boolean = false;
  hideUpgrades: boolean = false;
  hideConsumed: boolean = false;
  hideDuped: boolean = false;
  hideMissingUpgrades: boolean = false;
  showNonPremium: boolean = false;
  resourceAverageDays: number = 7;
  resourcePriceType: PriceType = PRICE_TYPES.sell;
  buyLocations: Location[] = [LOCATIONS.Caerleon];
  smugglerBuyLocation: Location | null = null;
  sellLocations: Location[] = [LOCATIONS.BlackMarket];
  tiers: number[] = [];
  upgradeFroms: number[] = [];
  upgradeTos: number[] = [];
  maxUpgradeResourcesCount: number | null = null;
  minBuyOrderAmount: number | null = null;
  upgradeResourcesPrices: { uniqueName: string; price: number }[] = [];
  upgradeResourcesAmounts: { uniqueName: string; amount: number | null }[] = [];

  constructor() { }

  static fromJson(json: string): FlipperConfiguration {
    const plainObject = JSON.parse(json);

    const result: FlipperConfiguration = Object.assign(new FlipperConfiguration(), plainObject);

    result.buyLocations = result.buyLocations.map((location: Location) => getLocation(Number(location.index)));
    result.sellLocations = result.sellLocations.map((location: Location) => getLocation(Number(location.index)));
    result.smugglerBuyLocation = result.smugglerBuyLocation ? getLocation(Number(result.smugglerBuyLocation.index)) : null;

    result.resourcePriceType = getPriceTypeByName(result.resourcePriceType.name);

    return result;
  }
}
